import React, { useMemo } from 'react'
import { GeneralWidgetConfig, shouldApplyBorderRadius, useSelectableWidgetOptions } from '@/components/WidgetConfiguration'
import { AnyConfigDesc, SettingsSection, WidgetFieldsForm } from '@/components/editor/widget-editor/form-fields'

const PANEL_STYLE_OPTIONS = [
  { label: 'Selecteer een style', value: '' },
  { label: 'standaard', value: 'standaard' },
  { label: 'no scrollBars', value: 'noScrollBars' },
  { label: 'toolbar', value: 'toolbar' },
  { label: 'border', value: 'border' },
  { label: 'border 2', value: 'border2' },
  { label: 'paper', value: 'paper' },
  { label: 'paper 2', value: 'paper2' },
  { label: 'padding', value: 'padding1' },
  { label: 'padding 2', value: 'padding2' },
  { label: 'padding 3', value: 'padding3' },
  { label: 'none', value: 'none' },
  { label: 'none white', value: 'noneWhite' },
  { label: 'refresh', value: 'refresh' },
  { label: 'three color template', value: 'threeColorTemplate' },
]

/** A setting every widget has, whatever its type */
type CommonOption = Omit<AnyConfigDesc, 'name' | 'type'> & {
  name: keyof GeneralWidgetConfig
  type: 'text' | 'boolean' | 'searchselect'
}

const VISIBILITY: CommonOption[] = [
  {
    name: 'hidden',
    type: 'text',
    label: 'hide this panel. Parameter must be true or false',
    topicValueInterpolationEnabled: true,
  },
  {
    name: 'hiddenwhenparametersareempty',
    type: 'text',
    label: 'comma separated parameters that need to have a value',
    advanced: true,
  },
]

const LOADING_BEHAVIOUR: CommonOption[] = [
  {
    name: 'showLoadingOverlay',
    type: 'boolean',
    label: 'Show loading overlay while loading.',
    advanced: true,
  },
  {
    name: 'syncWithUserActionsDisabled',
    type: 'boolean',
    label: 'User interaction disabled while other widgets are loading.',
    advanced: true,
  },
  {
    name: 'voteForUserActionsDisabledWhenLoading',
    type: 'boolean',
    label: 'Widget blocks user interaction on other widgets while loading.',
    advanced: true,
  },
]

const PANEL_STYLING: CommonOption[] = [
  {
    name: 'panelstyle',
    type: 'searchselect',
    label: 'panel style',
    placeholder: 'Search panel styles...',
    noResultsLabel: 'No styles found',
    options: PANEL_STYLE_OPTIONS,
  },
  {
    name: 'panelstyleExtra',
    type: 'text',
    label: 'panel style  (noScrollBars)',
    advanced: true,
  },
  {
    name: 'titleMenubar',
    type: 'text',
    label: 'title menubar',
    advanced: true,
  },
  {
    name: 'infoMenubar',
    type: 'text',
    label: 'Info popup menubar',
    advanced: true,
  },
  {
    name: 'colorPrimaryAction',
    type: 'boolean',
    label: 'give primary action color (when panel style "three color template")',
    advanced: true,
  },
  {
    name: 'fontFamily',
    type: 'text',
    label: 'CSS font-family definition for this widget, overrides default font',
    placeholder: "'Exo 2 Variable', sans-serif",
    advanced: true,
  },
]

const BORDER_RADIUS: CommonOption[] = [
  {
    name: 'finalTopLeftBorderRadius',
    type: 'text',
    label: 'final top-left border radius',
    advanced: true,
  },
  {
    name: 'finalTopRightBorderRadius',
    type: 'text',
    label: 'final border top-right radius',
    advanced: true,
  },
  {
    name: 'finalBottomLeftBorderRadius',
    type: 'text',
    label: 'final bottom-left border radius',
    advanced: true,
  },
  {
    name: 'finalBottomRightBorderRadius',
    type: 'text',
    label: 'final bottom-right border radius',
    advanced: true,
  },
]

/** The Common tab's options that can be advanced; identity never is */
export const COMMON_TAB_VARIABLES: ReadonlyArray<CommonOption> = [ ...VISIBILITY, ...LOADING_BEHAVIOUR ]

/** The panel options a widget type has */
export function panelStylingVariables(widget: GeneralWidgetConfig['widget']) {
  return shouldApplyBorderRadius(widget) ? [ ...PANEL_STYLING, ...BORDER_RADIUS ] : PANEL_STYLING
}

/** Settings every widget has, whatever its type */
export function CommonTab(props: { form: WidgetFieldsForm }) {
  const selectableWidgetOptions = useSelectableWidgetOptions()
  const identity = useMemo<CommonOption[]>(() => [
    {
      name: 'widget',
      type: 'searchselect',
      label: 'Widget type',
      placeholder: 'Search widgets...',
      noResultsLabel: 'No widgets found',
      options: selectableWidgetOptions,
    },
    {
      name: 'id',
      type: 'text',
      label: 'Area identifier (unique if set)',
    },
  ], [ selectableWidgetOptions ])

  return (
    <>
      <SettingsSection id="widget-identity"
                       header="Identity"
                       form={props.form}
                       variables={identity}
                       defaultExpanded />
      <SettingsSection id="widget-visibility"
                       header="Visibility"
                       form={props.form}
                       variables={VISIBILITY}
                       defaultExpanded />
      <SettingsSection id="widget-loading-behaviour"
                       header="Behaviour on loading"
                       form={props.form}
                       variables={LOADING_BEHAVIOUR} />
    </>
  )
}

/** How the panel around any widget looks; shown on the Styling tab */
export function PanelStylingSection(props: { form: WidgetFieldsForm }) {
  const widget = props.form.configDraft.widget
  const variables = useMemo(() => panelStylingVariables(widget), [ widget ])
  return (
    <SettingsSection id="widget-styling"
                     header="Panel"
                     form={props.form}
                     variables={variables}
                     defaultExpanded />
  )
}
